import React, { useState, useEffect } from 'react';
import { 
  XMarkIcon, 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  StarIcon,
  UserIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import useBookReviews from '../hooks/useBookReviews';
import ConfirmDialog from './ConfirmDialog';
import toast from 'react-hot-toast';

const BookReviewsModal = ({ isOpen, onClose, book }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [deleteReviewId, setDeleteReviewId] = useState(null);
  const [formData, setFormData] = useState({
    reviewerName: '',
    rating: 5,
    comment: '',
    email: ''
  });

  const {
    reviews,
    loading,
    addReview,
    updateReview,
    deleteReview,
    getReviewStats
  } = useBookReviews(book?.id);

  const reviewStats = getReviewStats();

  useEffect(() => {
    if (!isOpen) {
      setShowAddForm(false);
      setEditingReview(null);
      setFormData({
        reviewerName: '',
        rating: 5,
        comment: '',
        email: ''
      });
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRatingChange = (rating) => {
    setFormData(prev => ({
      ...prev,
      rating
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.reviewerName.trim() || !formData.comment.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editingReview) {
        await updateReview(editingReview.id, formData);
        setEditingReview(null);
        toast.success('Review updated successfully');
      } else {
        await addReview(formData);
        setShowAddForm(false);
        toast.success('Review added successfully');
      }
      
      setFormData({
        reviewerName: '',
        rating: 5,
        comment: '',
        email: ''
      });
    } catch (error) {
      console.error('Error saving review:', error);
    }
  };

  const handleEdit = (review) => {
    setEditingReview(review);
    setFormData({
      reviewerName: review.reviewerName || '',
      rating: review.rating || 5,
      comment: review.comment || '',
      email: review.email || ''
    });
    setShowAddForm(true);
  };

  const handleDelete = async () => {
    if (!deleteReviewId) return;
    
    try {
      await deleteReview(deleteReviewId);
      setDeleteReviewId(null);
    } catch (error) {
      console.error('Error deleting review:', error);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const renderStars = (rating, interactive = false, onChange = null) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type={interactive ? "button" : "button"}
            onClick={interactive ? () => onChange(star) : undefined}
            className={`${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'}`}
            disabled={!interactive}
          >
            {star <= rating ? (
              <StarIconSolid className="h-5 w-5 text-yellow-400" />
            ) : (
              <StarIcon className="h-5 w-5 text-gray-300" />
            )}
          </button>
        ))}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                Book Reviews - {book?.title}
              </h3>
              <p className="text-sm text-gray-500">
                Manage reviews and ratings for this book
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Review Statistics */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{reviewStats.total}</div>
                <div className="text-sm text-gray-500">Total Reviews</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{reviewStats.averageRating}</div>
                <div className="text-sm text-gray-500">Average Rating</div>
                <div className="flex justify-center mt-1">
                  {renderStars(reviewStats.averageRating)}
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm text-gray-500 mb-2">Rating Distribution</div>
                <div className="flex justify-center space-x-1">
                  {[5, 4, 3, 2, 1].map((rating) => (
                    <div key={rating} className="text-xs text-center">
                      <div className="font-medium">{rating}★</div>
                      <div className="text-gray-500">{reviewStats.ratingDistribution[rating] || 0}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Add Review Button */}
          {!showAddForm && (
            <div className="mb-6">
              <button
                onClick={() => setShowAddForm(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Review
              </button>
            </div>
          )}

          {/* Add/Edit Review Form */}
          {showAddForm && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-md font-medium text-gray-900">
                  {editingReview ? 'Edit Review' : 'Add New Review'}
                </h4>
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingReview(null);
                    setFormData({
                      reviewerName: '',
                      rating: 5,
                      comment: '',
                      email: ''
                    });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Reviewer Name *
                    </label>
                    <input
                      type="text"
                      name="reviewerName"
                      value={formData.reviewerName}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rating *
                  </label>
                  <div className="flex items-center space-x-2">
                    {renderStars(formData.rating, true, handleRatingChange)}
                    <span className="text-sm text-gray-500 ml-2">
                      {formData.rating} out of 5
                    </span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Comment *
                  </label>
                  <textarea
                    name="comment"
                    value={formData.comment}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddForm(false);
                      setEditingReview(null);
                      setFormData({
                        reviewerName: '',
                        rating: 5,
                        comment: '',
                        email: ''
                      });
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {editingReview ? 'Update Review' : 'Add Review'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Reviews List */}
          <div className="space-y-4">
            <h4 className="text-md font-medium text-gray-900">
              Reviews ({reviews.length})
            </h4>
            
            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block w-8 h-8 border-4 border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>
                <p className="mt-2 text-gray-500">Loading reviews...</p>
              </div>
            ) : reviews.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <StarIcon className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p>No reviews yet. Be the first to review this book!</p>
              </div>
            ) : (
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {reviews.map((review) => (
                  <div key={review.id} className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <div className="flex items-center space-x-1">
                            <UserIcon className="h-4 w-4 text-gray-400" />
                            <span className="font-medium text-gray-900">
                              {review.reviewerName || 'Anonymous'}
                            </span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <CalendarIcon className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-500">
                              {formatDate(review.createdAt)}
                            </span>
                          </div>
                        </div>
                        
                        <div className="mb-2">
                          {renderStars(review.rating)}
                        </div>
                        
                        <p className="text-gray-700">{review.comment}</p>
                        
                        {review.email && (
                          <p className="text-sm text-gray-500 mt-2">
                            Contact: {review.email}
                          </p>
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => handleEdit(review)}
                          className="text-gray-400 hover:text-blue-600 transition-colors"
                          title="Edit review"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setDeleteReviewId(review.id)}
                          className="text-gray-400 hover:text-red-600 transition-colors"
                          title="Delete review"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!deleteReviewId}
        onClose={() => setDeleteReviewId(null)}
        onConfirm={handleDelete}
        title="Delete Review"
        message="Are you sure you want to delete this review? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
    </>
  );
};

export default BookReviewsModal;
